/**
 * `<InlineTokenContextMenu>` — the right-click drop-down on a `#hashtag` or
 * entity-mention chip in the rich-text editor.
 *
 * Sibling of `InlineTokenModal` (which is the left-click modal with "Explore
 * this tag" navigation). This one stays small and positions at the cursor
 * because right-click drop-downs are an OS-level mental model — the user
 * expects a small popover at the click, not a centered modal.
 *
 * Behaviour:
 *   - Click outside the menu (anywhere on the page) → close
 *   - Esc → close
 *   - Cancel button → close
 *   - "Remove" button → invoke `onRemove` (parent does the document edit)
 *
 * Positioning: `position: fixed` at the mouse coordinates the caller captures
 * from the `contextmenu` MouseEvent. Edge-clamping nudges the menu back into
 * the viewport if the click lands near the right/bottom edge so it never
 * renders clipped off-screen.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { InlineTokenKind } from './InlineTokenModal';

export interface InlineTokenContextMenuProps {
  /** Mouse X (viewport coordinates, from MouseEvent.clientX). */
  x: number;
  /** Mouse Y (viewport coordinates, from MouseEvent.clientY). */
  y: number;
  /** Hashtag vs entity drives the menu label copy only. */
  kind: InlineTokenKind;
  /** The chip's surface text (`research` for `#research`, `Chris` for an entity). Shown as the menu eyebrow so the user knows what they're acting on. */
  label: string;
  /** Called when the user picks "Remove". The parent owns the document edit (this menu doesn't reach into the editor). */
  onRemove: () => void;
  /** Called on click-outside / Esc / Cancel. */
  onClose: () => void;
}

export function InlineTokenContextMenu({
  x,
  y,
  kind,
  label,
  onRemove,
  onClose,
}: InlineTokenContextMenuProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const removeButtonRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  // Click-outside → close. Use `mousedown` (not `click`) so a press outside
  // dismisses before the eventual click fires — matches OS context-menu feel.
  // Capture phase so we win against the editor's own mousedown handlers.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent): void => {
      if (panelRef.current === null) return;
      if (!panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', onMouseDown, { capture: true });
    return () => document.removeEventListener('mousedown', onMouseDown, { capture: true });
  }, [onClose]);

  // Esc → close. Capture phase so the editor's contentEditable doesn't eat it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onClose]);

  // Edge-clamp: after layout, measure ourselves and shift if the right/bottom
  // edge would clip the viewport. Layout effect (not effect) so the user
  // doesn't see a one-frame flash at the original click position.
  useLayoutEffect(() => {
    if (panelRef.current === null) return;
    const rect = panelRef.current.getBoundingClientRect();
    const margin = 6;
    let left = x;
    let top = y;
    if (left + rect.width + margin > window.innerWidth) {
      left = Math.max(margin, window.innerWidth - rect.width - margin);
    }
    if (top + rect.height + margin > window.innerHeight) {
      top = Math.max(margin, window.innerHeight - rect.height - margin);
    }
    setPos({ left, top });
  }, [x, y]);

  // Auto-focus Remove on open so Enter activates it (and so the menu has a
  // keyboard focus root for accessibility).
  useEffect(() => {
    removeButtonRef.current?.focus();
  }, []);

  const kindEyebrow = kind === 'hashtag' ? 'Hashtag' : 'Entity';
  const titleText = kind === 'hashtag' ? `#${label}` : label;
  const removeLabel = kind === 'hashtag' ? 'Remove tag' : 'Remove mention';

  return (
    <div
      ref={panelRef}
      className="inline-token-ctx"
      style={{ left: pos.left, top: pos.top }}
      role="menu"
      aria-label={`Token actions for ${titleText}`}
    >
      <div className="inline-token-ctx__eyebrow">{kindEyebrow}</div>
      <div className="inline-token-ctx__title">{titleText}</div>
      <button
        ref={removeButtonRef}
        type="button"
        className="inline-token-ctx__item inline-token-ctx__item--danger"
        role="menuitem"
        onClick={onRemove}
      >
        {removeLabel}
      </button>
      <button
        type="button"
        className="inline-token-ctx__item"
        role="menuitem"
        onClick={onClose}
      >
        Cancel
      </button>
    </div>
  );
}
